// Avocado Court's basketball minigame: a timed power-meter shootout. A marker
// sweeps a bar; tap SHOOT (or Space) when it's over the sweet zone to sink the
// basket — dead-center is a swish. Makes build a streak and the shots get
// harder (faster marker, tighter zone). This module owns the meter + scoring
// (DOM overlay); the court scene animates the 3D ball via the onShot callback.

const DURATION = 30;         // seconds per session
const LOCKOUT = 0.85;        // pause after a shot while the ball flies

export class Hoops {
  constructor({ onShot, onFinish } = {}){
    this.onShot = onShot || (() => {});
    this.onFinish = onFinish || (() => {});
    this.active = false;
    this.finished = false;

    this.timeLeft = DURATION;
    this.attempts = 0; this.makes = 0; this.swishes = 0; this.points = 0;
    this.streak = 0; this.bestStreak = 0;

    this.phase = 0;
    this.lockout = 0;

    this._onKey = this._onKey.bind(this);
  }

  get speed(){ return Math.min(6.0, 2.6 + this.makes * 0.25); }     // rad/s, ramps up
  get zoneW(){ return Math.max(5, 13 - this.makes * 0.6); }         // % half-width, shrinks

  mount(root){
    const el = document.createElement('div');
    el.className = 'hoop-hud';
    el.innerHTML = `
      <div class="hoop-info">
        <span class="pill hoop-timer">0:30</span>
        <span class="pill hoop-score">0 pts</span>
        <span class="pill hoop-streak" hidden></span>
      </div>
      <div class="hoop-feedback"></div>
      <div class="hoop-bar">
        <div class="hoop-zone"></div>
        <div class="hoop-marker"></div>
      </div>
      <button class="hoop-shoot">🏀 SHOOT</button>`;
    (root || document.body).appendChild(el);
    this.el = el;
    this.timerEl = el.querySelector('.hoop-timer');
    this.scoreEl = el.querySelector('.hoop-score');
    this.streakEl = el.querySelector('.hoop-streak');
    this.feedbackEl = el.querySelector('.hoop-feedback');
    this.zoneEl = el.querySelector('.hoop-zone');
    this.markerEl = el.querySelector('.hoop-marker');

    el.querySelector('.hoop-shoot').addEventListener('pointerdown', e => { e.preventDefault(); this._shoot(); });
    el.querySelector('.hoop-bar').addEventListener('pointerdown', e => { e.preventDefault(); this._shoot(); });
    window.addEventListener('keydown', this._onKey);
    this._renderZone();
  }

  begin(){ this.active = true; }

  _onKey(e){
    if (!this.active) return;
    if (e.code === 'Space' || e.code === 'Enter'){ e.preventDefault(); this._shoot(); }
  }

  _renderZone(){
    if (!this.zoneEl) return;
    const w = this.zoneW * 2;
    this.zoneEl.style.left = `${50 - this.zoneW}%`;
    this.zoneEl.style.width = `${w}%`;
  }

  _shoot(){
    if (!this.active || this.lockout > 0) return;
    const pos = 50 + 50 * Math.sin(this.phase);       // 0..100
    const dist = Math.abs(pos - 50);                   // 0..50 from center
    this.attempts++;
    this.lockout = LOCKOUT;

    const made = dist <= this.zoneW;
    const swish = dist <= this.zoneW * 0.35;
    if (made){
      this.makes++;
      if (swish) this.swishes++;
      this.streak++;
      this.bestStreak = Math.max(this.bestStreak, this.streak);
      this.points += swish ? 3 : 2;
      this._feedback(swish ? 'SWISH! +3' : 'bucket! +2', swish ? 'swish' : 'made');
    } else {
      this.streak = 0;
      this._feedback('miss', 'miss');
    }
    this._renderZone();
    this._renderScore();
    this.onShot({ made, swish, streak: this.streak });
  }

  update(dt){
    if (!this.active || this.finished) return;

    this.timeLeft -= dt;
    if (this.timeLeft <= 0){ this.timeLeft = 0; this._renderTimer(); return this._finish(); }
    this._renderTimer();

    if (this.lockout > 0){ this.lockout -= dt; return; }   // marker frozen while ball flies
    this.phase += this.speed * dt;
    const pos = 50 + 50 * Math.sin(this.phase);
    this.markerEl.style.left = `${pos}%`;
  }

  _renderTimer(){
    const s = Math.ceil(this.timeLeft);
    this.timerEl.textContent = `0:${String(s).padStart(2, '0')}`;
    this.timerEl.classList.toggle('low', s <= 5);
  }

  _renderScore(){
    this.scoreEl.textContent = `${this.points} pts`;
    if (this.streak >= 2){
      this.streakEl.hidden = false;
      this.streakEl.textContent = `🔥 ${this.streak} streak`;
      this.streakEl.classList.remove('pulse'); void this.streakEl.offsetWidth; this.streakEl.classList.add('pulse');
    } else {
      this.streakEl.hidden = true;
    }
  }

  _feedback(text, cls){
    if (!this.feedbackEl) return;
    this.feedbackEl.textContent = text;
    this.feedbackEl.className = `hoop-feedback show ${cls}`;
    void this.feedbackEl.offsetWidth;
    this.feedbackEl.classList.add('anim');
  }

  _finish(){
    if (this.finished) return;
    this.finished = true;
    this.active = false;
    const crumbs = this.makes + this.swishes + Math.floor(this.bestStreak / 3);
    this.onFinish({
      attempts: this.attempts, makes: this.makes, swishes: this.swishes,
      points: this.points, bestStreak: this.bestStreak, crumbs
    });
  }

  unmount(){
    window.removeEventListener('keydown', this._onKey);
    if (this.el) this.el.remove();
    this.el = null;
  }
}
